import { Spinner } from './feedback';
import { OperationBoundary } from './OperationBoundary';
import { useState } from 'react';
import { Link } from 'react-router';
import { ERROR_START_REASON } from '@mcpanel/core';
import { api } from '../api/client';
import type { ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Dialog } from './ui';

type Action = 'start' | 'stop' | 'stop-vm' | 'restart';

const confirmText: Record<Exclude<Action, 'start'>, { title: string; body: string; button: string }> = {
  stop: {
    title: 'サーバーを停止しますか？',
    body: 'ワールドを保存してから Minecraft を停止します。接続中のプレイヤーは切断されます。VM は動いたままです。',
    button: '停止',
  },
  'stop-vm': {
    title: 'サーバーと VM を停止しますか？',
    body: 'Minecraft を停止したあと、VM もシャットダウンしてメモリを解放します。次回の起動は少し時間がかかります。',
    button: 'VM ごと停止',
  },
  restart: {
    title: 'サーバーを再起動しますか？',
    body: '接続中のプレイヤーは一度切断されます。',
    button: '再起動',
  },
};

function ServerActionsContent({
  server,
  onDone,
  compact = false,
  restoreSnapshot,
}: {
  server: ServerView;
  onDone: () => void;
  compact?: boolean;
  restoreSnapshot?: string;
}) {
  const { canOperate, notify } = useApp();
  const [confirm, setConfirm] = useState<Exclude<Action, 'start'>>();
  const [busy, setBusy] = useState(false);

  if (!canOperate) return null;

  const run = async (action: Action) => {
    setConfirm(undefined);
    setBusy(true);
    try {
      if (action === 'start') await api.start(server.id);
      if (action === 'stop') await api.stop(server.id);
      if (action === 'stop-vm') await api.stop(server.id, true);
      if (action === 'restart') await api.restart(server.id);
      notify(
        `${server.name}: ${action === 'start' ? '起動' : action === 'restart' ? '再起動' : '停止'}を受け付けました`,
      );
      onDone();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const transitional = ['starting', 'stopping', 'provisioning', 'deleting'].includes(server.state);
  const size = compact ? ' small' : '';
  // 「VM ごと停止」は Minecraft が止まった時点で状態が「停止」になり、そのあと VM のシャットダウンを待つ
  const vmStopping = !!server.vm?.stopping;
  const vmStopped = server.vm?.running === false;

  return (
    <div className="row">
      {(server.state === 'stopped' || server.state === 'error') && (
        <button
          className={`btn primary${size}`}
          disabled={busy || server.state === 'error'}
          onClick={() => void run('start')}
        >
          {busy && <Spinner />}
          起動
        </button>
      )}
      {server.state === 'error' && (
        <>
          <button className={`btn${size}`} disabled>
            再起動
          </button>
          <div className="alert warn small" role="alert">
            <p>{ERROR_START_REASON}</p>
            <Link to={`/servers/${server.id}?tab=backups`}>
              {restoreSnapshot ? `変更の前のスナップショット（${restoreSnapshot}）に戻す` : 'スナップショットから戻す'}
            </Link>
            {' ・ '}
            <Link to={`/servers/${server.id}?tab=settings&section=software`}>ソフトとバージョンの変更をやり直す</Link>
          </div>
        </>
      )}
      {server.state === 'running' && (
        <>
          <button className={`btn${size}`} disabled={busy} onClick={() => setConfirm('restart')}>
            {busy && <Spinner />}
            再起動
          </button>
          <button className={`btn danger${size}`} disabled={busy} onClick={() => setConfirm('stop')}>
            {busy && <Spinner />}
            停止
          </button>
        </>
      )}
      {!compact && server.state !== 'provisioning' && !transitional && !vmStopping && !vmStopped && (
        <button className={`btn ghost${size}`} disabled={busy} onClick={() => setConfirm('stop-vm')}>
          {busy && <Spinner />}
          VM ごと停止
        </button>
      )}
      {transitional && !vmStopping && <span className="faint small">処理中…</span>}
      {!compact && vmStopping && (
        <span className="faint small">
          <Spinner /> VM をシャットダウン中…
        </span>
      )}
      {!compact && vmStopped && !vmStopping && (
        <span className="faint small">VM も停止済み（メモリを解放しています）</span>
      )}
      {confirm && (
        <Dialog
          title={confirmText[confirm].title}
          onClose={() => setConfirm(undefined)}
          actions={
            <>
              <button className="btn" onClick={() => setConfirm(undefined)}>
                キャンセル
              </button>
              <button className="btn danger-solid" onClick={() => void run(confirm)}>
                {confirmText[confirm].button}
              </button>
            </>
          }
        >
          <p>
            <strong>{server.name}</strong> — {confirmText[confirm].body}
          </p>
          {server.live && server.live.players > 0 && (
            <div className="alert warn">現在 {server.live.players} 人が接続しています。</div>
          )}
        </Dialog>
      )}
    </div>
  );
}

export function ServerActions(props: Parameters<typeof ServerActionsContent>[0]) {
  return (
    <OperationBoundary serverId={props.server.id}>
      <ServerActionsContent {...props} />
    </OperationBoundary>
  );
}
